/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Contract: idl/agent/prompt_history.thrift
import type { HarnessConfig } from '@adp-arch/idl/playground_api';

import { axiosInstance } from './axios';

export interface PromptRevision {
  id: string;
  version: number;
  prompt: string;
  note: string;
  source:
    | 'manual'
    | 'before_restore'
    | 'restore'
    | 'before_optimize'
    | 'optimized';
  author_id: string;
  created_at: number;
}
interface Response {
  code: number;
  msg: string;
}
async function request<T extends Response>(
  action: string,
  data: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const result = await axiosInstance.request<unknown, T>({
    url: `/api/agent_prompt/${action}`,
    method: 'POST',
    data,
    signal,
    timeout: 100000,
  });
  if (result.code !== 0) {
    throw new Error(result.msg || 'Request failed');
  }
  return result;
}
export const promptHistoryApi = {
  harnessTemplate: (agentId: string, signal?: AbortSignal) =>
    request<
      Response & {
        prompt: string;
        template_version: string;
        flash_instruction: string;
        pro_instruction: string;
        defaults: HarnessConfig;
      }
    >('harness_template', { agent_id: agentId }, signal),
  models: (agentId: string, signal?: AbortSignal) =>
    request<Response & { models: Array<{ id: string; name: string }> }>(
      'models',
      { agent_id: agentId },
      signal,
    ),
  list: (
    agentId: string,
    {
      kind = 'persona',
      query = '',
      cursor = '',
      signal,
    }: {
      kind?: string;
      query?: string;
      cursor?: string;
      signal?: AbortSignal;
    } = {},
  ) =>
    request<Response & { revisions: PromptRevision[]; next_cursor: string }>(
      'list',
      { agent_id: agentId, query, cursor, kind },
      signal,
    ),
  save: (
    agentId: string,
    {
      prompt,
      note,
      source,
      requestId,
      kind = 'persona',
    }: {
      prompt: string;
      note: string;
      source: PromptRevision['source'];
      requestId: string;
      kind?: string;
    },
  ) =>
    request<Response & { revision: PromptRevision }>('save', {
      agent_id: agentId,
      prompt,
      note,
      source,
      request_id: requestId,
      kind,
    }),
  optimize: (
    agentId: string,
    {
      prompt,
      instructions,
      modelId,
      signal,
    }: {
      prompt: string;
      instructions: string;
      modelId: string;
      signal?: AbortSignal;
    },
  ) =>
    request<Response & { prompt: string }>(
      'optimize',
      { agent_id: agentId, prompt, instructions, model_id: modelId },
      signal,
    ),
};

export interface SandboxTool {
  name: string;
  kind: 'command' | 'python_package' | 'node_package';
  version: string;
  description: string;
  example: string;
}
export interface SandboxEnvironment {
  browser_enabled?: boolean;
  type?: 'daytona' | 'openshell' | 'opensandbox';
  network_allowed_hosts?: string[];
  id: string;
  name: string;
  revision: string;
  snapshot: string;
  network_block_all: boolean;
  tools: SandboxTool[];
}
export const getHarnessEnvironments = (agentId: string, signal?: AbortSignal) =>
  request<
    Response & { default_id: string; environments: SandboxEnvironment[] }
  >('environment', { agent_id: agentId }, signal);
